import React from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Users, MailCheck, MailWarning, Download, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loading } from "@/components/shared";
import api from "@/lib/api";

function Stat({ icon: Icon, label, value, tone = "navy" }) {
  const tones = { navy: "text-navy bg-navy/5", emerald: "text-emerald bg-emerald/10", amber: "text-orange-600 bg-orange-50" };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone]}`}><Icon className="h-[18px] w-[18px]" strokeWidth={1.75} /></div>
      <p className="mt-3 font-heading text-2xl font-bold text-navy tabular-nums">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
    </div>
  );
}

export default function SubscribersAdmin() {
  const { data, isLoading } = useQuery({ queryKey: ["admin-subscribers"], queryFn: async () => (await api.get("/admin/subscribers")).data });
  if (isLoading) return <Loading />;
  const exportCsv = async () => {
    try { const res = await api.get("/admin/subscribers.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "subscribers.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const sources = Object.entries(data.by_source || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div data-testid="subscribers-admin">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-heading text-xl font-semibold text-navy">Subscribers</h2>
        <Button variant="outline" onClick={exportCsv} data-testid="subs-csv"><Download className="mr-1.5 h-4 w-4" /> CSV</Button>
      </div>

      {!data.resend_configured && (
        <div className="mb-6 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800" data-testid="resend-notice">
          <strong>Email sending is not live yet.</strong> {data.queued_emails} email(s) are queued. To start sending the daily post, set
          <span className="mx-1 font-mono">RESEND_API_KEY</span> in <span className="font-mono">/app/backend/.env</span> and restart the backend. Sender name is “Shubham Bapna”.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label="Total" value={data.total} />
        <Stat icon={MailCheck} label="Confirmed" value={data.confirmed} tone="emerald" />
        <Stat icon={MailWarning} label="Unconfirmed" value={data.unconfirmed} tone="amber" />
        <Stat icon={Clock} label="Emails queued" value={data.queued_emails} tone="amber" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-6 lg:col-span-2">
          <h3 className="font-heading font-semibold text-navy">Growth (last 30 days)</h3>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} interval={4} />
                <YAxis stroke="#94a3b8" fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="signups" fill="#0E8A62" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <h3 className="font-heading font-semibold text-navy">By source</h3>
          <div className="mt-4 space-y-3" data-testid="source-split">
            {sources.length === 0 && <p className="text-sm text-slate-400">No subscribers yet.</p>}
            {sources.map(([src, n]) => {
              const pct = data.total ? Math.round((n / data.total) * 100) : 0;
              return (
                <div key={src}>
                  <div className="flex justify-between text-sm"><span className="font-medium text-navy">{src}</span><span className="text-slate-500 tabular-nums">{n}</span></div>
                  <div className="mt-1 h-1.5 w-full rounded bg-slate-100"><div className="h-1.5 rounded bg-emerald" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="subscribers-table">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>{["Email", "Name", "Source", "Status", "Joined"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data.subscribers || []).map((s) => (
              <tr key={s.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-navy">{s.email}</td>
                <td className="px-4 py-3 text-slate-600">{s.first_name || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{s.source}</td>
                <td className="px-4 py-3">
                  {s.unsubscribed_at ? <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">unsubscribed</span>
                    : s.confirmed ? <span className="rounded bg-emerald/10 px-2 py-0.5 text-xs font-semibold text-emerald">confirmed</span>
                    : <span className="rounded bg-orange-50 px-2 py-0.5 text-xs font-semibold text-orange-600">pending</span>}
                </td>
                <td className="px-4 py-3 text-slate-500 tabular-nums">{(s.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
            {(data.subscribers || []).length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No subscribers yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
